import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);

      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      localStorage.setItem('admin_token', response.data.access_token);
      
      toast({
        title: "Login realizado com sucesso!",
        description: "Redirecionando para o painel...",
      });

      setTimeout(() => {
        navigate('/admin-painel-gestao/dashboard');
      }, 500);
    } catch (error) {
      console.error('Erro no login:', error);
      toast({
        title: "Erro ao fazer login",
        description: error.response?.data?.detail || "Email ou senha incorretos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#EA1266]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#64BC46]/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8">
          
          {/* Logo e Titulo */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-[#EA1266] rounded-2xl blur-xl opacity-20" />
              <div className="relative bg-[#EA1266] w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl shadow-pink-500/20">
                <Sparkles size={32} className="text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-medium text-[#0A0A0A] mb-2">
              Painel de Gestão
            </h1>
            <p className="text-sm text-[#4B5563] font-medium">Disneylândia Cosméticos e Presentes</p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleLogin} className="space-y-5" autoComplete="off">
            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-2 uppercase tracking-wider">
                Email
              </label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA1266] transition-colors" size={18} />
                <input
                  type="email"
                  name="login_email"
                  autoComplete="off"
                  placeholder="Digite seu email"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="w-full pl-12 pr-4 h-12 bg-gray-50 border border-gray-200 rounded-xl text-[#0A0A0A] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#EA1266]/30 focus:border-[#EA1266] transition-all text-sm"
                  data-testid="admin-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#4B5563] mb-2 uppercase tracking-wider">
                Senha
              </label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-[#EA1266] transition-colors" size={18} />
                <input
                  type="password"
                  name="login_password"
                  autoComplete="new-password"
                  placeholder="Digite sua senha"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="w-full pl-12 pr-4 h-12 bg-gray-50 border border-gray-200 rounded-xl text-[#0A0A0A] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#EA1266]/30 focus:border-[#EA1266] transition-all text-sm"
                  data-testid="admin-password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EA1266] hover:bg-[#d00f58] text-white font-medium h-12 rounded-full shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 transition-all hover:scale-[1.02] text-sm uppercase tracking-wider"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Entrando...
                </span>
              ) : 'Acessar Painel'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-[#4B5563] font-medium">
              Acesso restrito a administradores
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Suporte técnico:{' '}
            <a 
              href="https://instagram.com/gfcomunicacaoo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#EA1266] hover:text-[#d00f58] font-medium transition-colors"
            >
              @gfcomunicacaoo
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
